import { FC, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useAuth } from '../../auth'
import {
  createVendor,
  getCurrencies,
  getVendorCategories,
  getVendorCompanyGroupOptions,
  searchRegions,
  updateVendor,
} from '../core/_requests'
import type { CurrencyOption, VendorCompanyGroupOption } from '../core/_requests'
import type {
  CreateVendorRequest,
  RegionOption,
  Vendor,
  VendorCategory,
  VendorCompanyContact,
  VendorCompanyMapping,
} from '../core/_models'
import {
  actorCanPickAnyCompanyGroup,
  resolveActorCompanyAdminId,
  resolveEffectiveBehaviorType,
  resolveEffectiveCompanyGroupId,
} from '../core/adminActorContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import CurrencySelect from './CurrencySelect'
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage'

type MappingForm = VendorCompanyMapping & { clientId: string }

interface AddVendorModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  vendor?: Vendor | null
  vendorCategories?: VendorCategory[]
}

const formLabelClassName = 'form-label fw-semibold mb-2 text-gray-800'
const sectionHeadingClassName = 'fw-bold text-gray-900 mb-3'
const helperTextClassName = 'small text-gray-700'
const errorTextClassName = 'text-danger small mt-1'
const mutedCardStyle = { backgroundColor: '#f8f9fa', borderColor: '#dce1e7' } as const

const makeContact = (): VendorCompanyContact => ({
  name: '',
  email: '',
  phone: '',
  designation: '',
  primaryContact: true,
  active: true,
})

const makeMapping = (): MappingForm => ({
  clientId: Math.random().toString(36).slice(2),
  id: undefined,
  companyGroupAdminId: null,
  companyAdminId: null,
  code: '',
  vendorInitials: '',
  name: '',
  contactPerson: '',
  email: '',
  contactNo: '',
  altContactNo: '',
  address: '',
  paymentTerms: '',
  tag: 'NORMAL',
  categoryIds: [],
  contacts: [makeContact()],
  operatingRegions: [],
  serviceRegionIds: [],
  serviceRegions: [],
  totalOrders: 0,
  avgDeliveryDays: 0,
  issueCount: 0,
  active: true,
})

const AddVendorModal: FC<AddVendorModalProps> = ({ visible, onClose, onSubmit, vendor, vendorCategories: parentCategories = [] }) => {
  const { currentUser } = useAuth()
  const isEditMode = Boolean(vendor?.id)
  const actorBehavior = resolveEffectiveBehaviorType(currentUser)
  const canManageMultiCompanyMappings = actorCanPickAnyCompanyGroup(currentUser)
  const myCompanyGroupAdminId =
    resolveEffectiveCompanyGroupId(currentUser) ??
    (currentUser as any)?.companyGroupAdminId ??
    (currentUser as any)?.companyGroupAdmin?.id ??
    (currentUser as any)?.cgaid?.id ??
    (typeof (currentUser as any)?.cgaid === 'number' ? (currentUser as any).cgaid : null) ??
    null
  const myCompanyAdminId =
    resolveActorCompanyAdminId(currentUser) ??
    (currentUser as any)?.companyAdminId ??
    (currentUser as any)?.companyAdmin?.id ??
    null
  const isScopedActor = isEditMode || (!canManageMultiCompanyMappings && (myCompanyAdminId != null || myCompanyGroupAdminId != null))

  const [companyGroups, setCompanyGroups] = useState<VendorCompanyGroupOption[]>([])
  const [vendorCategories, setVendorCategories] = useState<VendorCategory[]>(parentCategories)
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [regionSearchTerms, setRegionSearchTerms] = useState<Record<string, string>>({})
  const [regionSearchResults, setRegionSearchResults] = useState<Record<string, RegionOption[]>>({})
  const [regionSearchLoading, setRegionSearchLoading] = useState<Record<string, boolean>>({})
  const [globalForm, setGlobalForm] = useState({
    code: '',
    vendorInitials: '',
    name: '',
    email: '',
    password: '',
    contactNo: '',
    altContactNo: '',
    contactPerson: '',
    address: '',
    paymentTerms: '',
    preferredCurrencyCode: 'INR',
    bankBeneficiaryName: '',
    bankName: '',
    bankAccountNumber: '',
    bankIfscCode: '',
    bankSwiftCode: '',
    bankIban: '',
    bankBranch: '',
    bankAddress: '',
    bankAccountCurrency: '',
    tag: 'NORMAL',
  })
  const [mappings, setMappings] = useState<MappingForm[]>([makeMapping()])

  const scopedMapping = mappings[0] ?? makeMapping()

  useEffect(() => {
    setVendorCategories(parentCategories)
  }, [parentCategories])

  useEffect(() => {
    if (!visible) return
    void loadSupportData()
    hydrateForm()
  }, [visible, vendor, canManageMultiCompanyMappings, myCompanyAdminId, myCompanyGroupAdminId])

  const applyActorScope = (mapping: MappingForm): MappingForm => {
    if (canManageMultiCompanyMappings) {
      return mapping
    }
    if (myCompanyAdminId != null) {
      return { ...mapping, companyAdminId: myCompanyAdminId, companyGroupAdminId: null }
    }
    if (myCompanyGroupAdminId != null) {
      return { ...mapping, companyAdminId: null, companyGroupAdminId: myCompanyGroupAdminId }
    }
    return mapping
  }

  const normalizeContacts = (
    contacts?: VendorCompanyContact[],
    fallbackName = '',
    fallbackEmail = '',
    fallbackPhone = ''
  ) => {
    if (!contacts?.length) {
      const name = fallbackName || 'Primary Contact'
      return [
        {
          name,
          email: fallbackEmail || '',
          phone: fallbackPhone || '',
          designation: '',
          primaryContact: true,
          active: true,
        },
      ]
    }
    const hasPrimary = contacts.some((contact) => contact.primaryContact)
    return contacts.map((contact, index) => ({
      ...makeContact(),
      ...contact,
      name: contact.name || fallbackName || (index === 0 ? 'Primary Contact' : `Contact ${index + 1}`),
      primaryContact: Boolean(contact.primaryContact) || (!hasPrimary && index === 0),
    }))
  }

  const hydrateForm = () => {
    if (!vendor) {
      const next = applyActorScope(makeMapping())
      setGlobalForm({
        code: '',
        vendorInitials: '',
        name: '',
        email: '',
        password: '',
        contactNo: '',
        altContactNo: '',
        contactPerson: '',
        address: '',
        paymentTerms: '',
        preferredCurrencyCode: 'INR',
        bankBeneficiaryName: '',
        bankName: '',
        bankAccountNumber: '',
        bankIfscCode: '',
        bankSwiftCode: '',
        bankIban: '',
        bankBranch: '',
        bankAddress: '',
        bankAccountCurrency: '',
        tag: 'NORMAL',
      })
      setMappings([next])
      setErrors({})
      setRegionSearchTerms({})
      setRegionSearchResults({})
      setRegionSearchLoading({})
      return
    }

    const baseMappings = vendor.companyMappings?.length
      ? vendor.companyMappings
      : [{
        id: vendor.vendorCompanyMappingId ?? undefined,
        companyGroupAdminId: vendor.cgaid ?? null,
        companyAdminId: vendor.caId ?? null,
        code: vendor.code,
        vendorInitials: vendor.vendorInitials || '',
        name: vendor.name,
        contactPerson: vendor.contactPerson || '',
        email: vendor.email,
        contactNo: vendor.contactNo,
        altContactNo: vendor.altContactNo,
        address: vendor.address || '',
        paymentTerms: vendor.paymentTerms || '',
        tag: vendor.tag,
        categoryIds: vendor.categoryIds || [],
        contacts: vendor.contacts?.length ? vendor.contacts : [],
        operatingRegions: vendor.operatingRegions || [],
        serviceRegionIds: vendor.serviceRegionIds || [],
        serviceRegions: vendor.serviceRegions || [],
        totalOrders: vendor.totalOrders || 0,
        avgDeliveryDays: vendor.avgDeliveryDays || 0,
        issueCount: vendor.issueCount || 0,
        active: true,
      }]

    const hydratedMappings = baseMappings.map((mapping) =>
      applyActorScope({
        clientId: Math.random().toString(36).slice(2),
        id: mapping.id,
        companyGroupAdminId: mapping.companyGroupAdminId ?? null,
        companyAdminId: mapping.companyAdminId ?? null,
        code: mapping.code || vendor.code || '',
        vendorInitials: mapping.vendorInitials || vendor.vendorInitials || '',
        name: mapping.name || vendor.name || '',
        contactPerson: mapping.contactPerson || vendor.contactPerson || '',
        email: mapping.email || vendor.email || '',
        contactNo: mapping.contactNo || vendor.contactNo || '',
        altContactNo: mapping.altContactNo || vendor.altContactNo || '',
        address: mapping.address || vendor.address || '',
        paymentTerms: mapping.paymentTerms || vendor.paymentTerms || '',
        tag: mapping.tag || vendor.tag || 'NORMAL',
        categoryIds: mapping.categoryIds || [],
        categoryNames: mapping.categoryNames,
        contacts: normalizeContacts(
          mapping.contacts,
          mapping.contactPerson || vendor.contactPerson || vendor.name,
          mapping.email || vendor.email,
          mapping.contactNo || vendor.contactNo
        ),
        operatingRegions: mapping.operatingRegions || [],
        serviceRegionIds: mapping.serviceRegionIds || [],
        serviceRegions: mapping.serviceRegions || [],
        totalOrders: mapping.totalOrders || 0,
        avgDeliveryDays: mapping.avgDeliveryDays || 0,
        issueCount: mapping.issueCount || 0,
        active: mapping.active ?? true,
      })
    )

    const scopedVisibleMapping =
      hydratedMappings.find((mapping) =>
        myCompanyAdminId != null
          ? mapping.companyAdminId === myCompanyAdminId
          : mapping.companyAdminId == null && mapping.companyGroupAdminId === myCompanyGroupAdminId
      ) ?? hydratedMappings[0]

    setGlobalForm({
      code: vendor.code || scopedVisibleMapping?.code || '',
      vendorInitials: vendor.vendorInitials || scopedVisibleMapping?.vendorInitials || '',
      name: vendor.name || scopedVisibleMapping?.name || '',
      email: vendor.email || scopedVisibleMapping?.email || '',
      password: vendor.password || '',
      contactNo: vendor.contactNo || scopedVisibleMapping?.contactNo || '',
      altContactNo: vendor.altContactNo || scopedVisibleMapping?.altContactNo || '',
      contactPerson: vendor.contactPerson || scopedVisibleMapping?.contactPerson || '',
      address: vendor.address || scopedVisibleMapping?.address || '',
      paymentTerms: vendor.paymentTerms || scopedVisibleMapping?.paymentTerms || '',
      preferredCurrencyCode: vendor.preferredCurrencyCode || 'INR',
      bankBeneficiaryName: vendor.bankBeneficiaryName || '',
      bankName: vendor.bankName || '',
      bankAccountNumber: vendor.bankAccountNumber || '',
      bankIfscCode: vendor.bankIfscCode || '',
      bankSwiftCode: vendor.bankSwiftCode || '',
      bankIban: vendor.bankIban || '',
      bankBranch: vendor.bankBranch || '',
      bankAddress: vendor.bankAddress || '',
      bankAccountCurrency: vendor.bankAccountCurrency || '',
      tag: vendor.tag || scopedVisibleMapping?.tag || 'NORMAL',
    })
    setMappings(canManageMultiCompanyMappings ? hydratedMappings : [scopedVisibleMapping])
    setErrors({})
    setRegionSearchTerms({})
    setRegionSearchResults({})
    setRegionSearchLoading({})
  }

  const loadSupportData = async () => {
    setIsLoadingCompanies(true)
    try {
      const [groups, categories, currencyRows] = await Promise.all([
        getVendorCompanyGroupOptions(),
        parentCategories.length ? Promise.resolve(parentCategories) : getVendorCategories(),
        getCurrencies().catch(() => []),
      ])
      setCompanyGroups(groups ?? [])
      setVendorCategories(categories ?? [])
      setCurrencies(currencyRows ?? [])
    } catch (error) {
      console.error('Error loading vendor support data:', error)
    } finally {
      setIsLoadingCompanies(false)
    }
  }

  const updateGlobalField = (name: string, value: string) => {
    setGlobalForm((prev) => ({ ...prev, [name]: value }))
    setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const updateMapping = (clientId: string, patch: Partial<MappingForm>) => {
    setMappings((prev) =>
      prev.map((mapping) => (mapping.clientId === clientId ? applyActorScope({ ...mapping, ...patch }) : mapping))
    )
  }

  const addMapping = () => {
    const next = makeMapping()
    next.code = globalForm.code
    next.vendorInitials = globalForm.vendorInitials
    next.name = globalForm.name
    next.email = globalForm.email
    next.contactNo = globalForm.contactNo
    next.altContactNo = globalForm.altContactNo
    next.contactPerson = globalForm.contactPerson
    next.address = globalForm.address
    next.paymentTerms = globalForm.paymentTerms
    next.tag = globalForm.tag
    setMappings((prev) => [...prev, next])
  }

  const removeMapping = (clientId: string) => {
    setMappings((prev) => (prev.length === 1 ? prev : prev.filter((mapping) => mapping.clientId !== clientId)))
  }

  const addContact = (clientId: string) => {
    setMappings((prev) =>
      prev.map((mapping) =>
        mapping.clientId === clientId
          ? { ...mapping, contacts: [...mapping.contacts, { ...makeContact(), primaryContact: false }] }
          : mapping
      )
    )
  }

  const removeContact = (clientId: string, index: number) => {
    setMappings((prev) =>
      prev.map((mapping) => {
        if (mapping.clientId !== clientId) return mapping
        const nextContacts = mapping.contacts.filter((_, contactIndex) => contactIndex !== index)
        if (!nextContacts.some((contact) => contact.primaryContact) && nextContacts[0]) {
          nextContacts[0] = { ...nextContacts[0], primaryContact: true }
        }
        return { ...mapping, contacts: nextContacts.length ? nextContacts : [makeContact()] }
      })
    )
  }

  const updateContact = (clientId: string, index: number, patch: Partial<VendorCompanyContact>) => {
    setMappings((prev) =>
      prev.map((mapping) => {
        if (mapping.clientId !== clientId) return mapping
        const contacts = mapping.contacts.map((contact, contactIndex) => {
          if (contactIndex !== index) {
            return patch.primaryContact ? { ...contact, primaryContact: false } : contact
          }
          return { ...contact, ...patch }
        })
        return { ...mapping, contacts }
      })
    )
  }

  const toggleCategory = (clientId: string, categoryId: number) => {
    setMappings((prev) =>
      prev.map((mapping) => {
        if (mapping.clientId !== clientId) return mapping
        const exists = mapping.categoryIds.includes(categoryId)
        return {
          ...mapping,
          categoryIds: exists ? mapping.categoryIds.filter((id) => id !== categoryId) : [...mapping.categoryIds, categoryId],
        }
      })
    )
  }

  const handleRegionSearch = async (clientId: string, term: string) => {
    setRegionSearchTerms((prev) => ({ ...prev, [clientId]: term }))
    if (term.trim().length < 2) {
      setRegionSearchResults((prev) => ({ ...prev, [clientId]: [] }))
      setRegionSearchLoading((prev) => ({ ...prev, [clientId]: false }))
      return
    }

    setRegionSearchLoading((prev) => ({ ...prev, [clientId]: true }))
    try {
      const results = await searchRegions(term.trim())
      setRegionSearchResults((prev) => ({ ...prev, [clientId]: results }))
    } finally {
      setRegionSearchLoading((prev) => ({ ...prev, [clientId]: false }))
    }
  }

  const addServiceRegion = (clientId: string, region: RegionOption) => {
    setMappings((prev) =>
      prev.map((mapping) =>
        mapping.clientId !== clientId || mapping.serviceRegionIds?.includes(region.id)
          ? mapping
          : {
            ...mapping,
            serviceRegionIds: [...(mapping.serviceRegionIds || []), region.id],
            serviceRegions: [...(mapping.serviceRegions || []), region],
            operatingRegions: [...(mapping.operatingRegions || []), region.path],
          }
      )
    )
    setRegionSearchTerms((prev) => ({ ...prev, [clientId]: '' }))
    setRegionSearchResults((prev) => ({ ...prev, [clientId]: [] }))
    setRegionSearchLoading((prev) => ({ ...prev, [clientId]: false }))
  }

  const removeServiceRegion = (clientId: string, regionId: number) => {
    setMappings((prev) =>
      prev.map((mapping) =>
        mapping.clientId !== clientId
          ? mapping
          : {
            ...mapping,
            serviceRegionIds: (mapping.serviceRegionIds || []).filter((id) => id !== regionId),
            serviceRegions: (mapping.serviceRegions || []).filter((region) => region.id !== regionId),
            operatingRegions: (mapping.serviceRegions || [])
              .filter((region) => region.id !== regionId)
              .map((region) => region.path),
          }
      )
    )
  }

  const validateForm = () => {
    const nextErrors: Record<string, string> = {}
    if (!globalForm.code.trim()) nextErrors.code = 'Vendor code is required'
    if (!globalForm.name.trim()) nextErrors.name = 'Vendor name is required'
    if (!globalForm.email.trim()) nextErrors.email = 'Email is required'
    if (!isEditMode && !globalForm.password.trim()) nextErrors.password = 'Password is required'
    if (!globalForm.contactNo.trim()) nextErrors.contactNo = 'Contact number is required'
    if (!canManageMultiCompanyMappings && !isScopedActor) nextErrors.companyMappings = 'Vendor scope could not be resolved'
    if (canManageMultiCompanyMappings && mappings.length === 0) nextErrors.companyMappings = 'At least one company mapping is required'

    const selections = new Set<string>()
    const mappingsToValidate = canManageMultiCompanyMappings ? mappings : [scopedMapping]
    mappingsToValidate.forEach((mapping, index) => {
      const key = `CGA:${mapping.companyGroupAdminId}`
      if (canManageMultiCompanyMappings && !isEditMode && mapping.companyGroupAdminId == null) {
        nextErrors[`mapping-${index}-scope`] = 'Select a company group'
      }
      if (canManageMultiCompanyMappings && selections.has(key)) {
        nextErrors[`mapping-${index}-duplicate`] = 'Duplicate company selection is not allowed'
      }
      selections.add(key)
      const effectiveCode = mapping.code?.trim() || globalForm.code.trim()
      const effectiveName = mapping.name?.trim() || globalForm.name.trim()
      if (!effectiveCode) nextErrors[`mapping-${index}-code`] = 'Vendor code is required'
      if (!effectiveName) nextErrors[`mapping-${index}-name`] = 'Vendor name is required'

      mapping.contacts.forEach((contact, contactIndex) => {
        const contactEffectiveName =
          contact.name?.trim() ||
          mapping.contactPerson?.trim() ||
          globalForm.contactPerson?.trim() ||
          globalForm.name.trim()
        if (!contactEffectiveName) {
          nextErrors[`mapping-${index}-contact-${contactIndex}-name`] = 'Contact name is required'
        }
      })
    })

    setErrors(nextErrors)
    const isValid = Object.keys(nextErrors).length === 0
    if (!isValid) {
      const firstError = Object.values(nextErrors)[0]
      if (firstError) {
        toast.error(firstError)
      }
    }
    return isValid
  }

  const toPayload = (): CreateVendorRequest => {
    const mappingsToSend = canManageMultiCompanyMappings
      ? mappings.map((mapping) => ({
        id: mapping.id,
        companyGroupAdminId: mapping.companyGroupAdminId,
        code: mapping.code?.trim() || globalForm.code.trim(),
        vendorInitials: mapping.vendorInitials || globalForm.vendorInitials,
        name: mapping.name?.trim() || globalForm.name.trim(),
        contactPerson: mapping.contactPerson || globalForm.contactPerson,
        email: mapping.email || globalForm.email,
        contactNo: mapping.contactNo || globalForm.contactNo,
        altContactNo: mapping.altContactNo || globalForm.altContactNo,
        address: mapping.address || globalForm.address,
        paymentTerms: mapping.paymentTerms || globalForm.paymentTerms,
        tag: mapping.tag || globalForm.tag,
        categoryIds: mapping.categoryIds,
        contacts: mapping.contacts,
        operatingRegions: mapping.operatingRegions || [],
        serviceRegionIds: mapping.serviceRegionIds || [],
        serviceRegions: mapping.serviceRegions || [],
        totalOrders: mapping.totalOrders,
        avgDeliveryDays: mapping.avgDeliveryDays,
        issueCount: mapping.issueCount,
        active: mapping.active ?? true,
      }))
      : []

    const sourceMapping = canManageMultiCompanyMappings ? mappingsToSend[0] : scopedMapping
    const primaryContact = sourceMapping?.contacts?.find((contact) => contact.primaryContact) ?? sourceMapping?.contacts?.[0]

    return {
      vendor: {
        id: vendor?.id,
        code: globalForm.code.trim(),
        vendorInitials: globalForm.vendorInitials,
        name: globalForm.name.trim(),
        email: globalForm.email.trim(),
        password: globalForm.password || undefined,
        contactNo: globalForm.contactNo.trim(),
        altContactNo: globalForm.altContactNo,
        contactPerson: primaryContact?.name || globalForm.contactPerson || globalForm.name,
        address: globalForm.address,
        paymentTerms: globalForm.paymentTerms,
        preferredCurrencyCode: globalForm.preferredCurrencyCode,
        bankBeneficiaryName: globalForm.bankBeneficiaryName,
        bankName: globalForm.bankName,
        bankAccountNumber: globalForm.bankAccountNumber,
        bankIfscCode: globalForm.bankIfscCode,
        bankSwiftCode: globalForm.bankSwiftCode,
        bankIban: globalForm.bankIban,
        bankBranch: globalForm.bankBranch,
        bankAddress: globalForm.bankAddress,
        bankAccountCurrency: globalForm.bankAccountCurrency,
        tag: globalForm.tag,
        categoryIds: canManageMultiCompanyMappings ? undefined : sourceMapping?.categoryIds || [],
        contacts: canManageMultiCompanyMappings ? undefined : sourceMapping?.contacts || [],
        operatingRegions: canManageMultiCompanyMappings ? undefined : sourceMapping?.operatingRegions || [],
        serviceRegionIds: canManageMultiCompanyMappings ? undefined : sourceMapping?.serviceRegionIds || [],
        serviceRegions: canManageMultiCompanyMappings ? undefined : sourceMapping?.serviceRegions || [],
      },
      companyMappings: mappingsToSend,
    }
  }

  const handleSubmit = async () => {
    if (!validateForm() || isSubmitting) return
    setIsSubmitting(true)
    try {
      const payload = toPayload()
      if (isEditMode && vendor) {
        await updateVendor(vendor.id, payload)
      } else {
        await createVendor(payload)
      }
      onSubmit()
      onClose()
    } catch (error: any) {
      console.error('Error saving vendor:', error)
      const msg = getApiErrorMessage(
        error,
        isEditMode ? 'Failed to update vendor.' : 'Failed to create vendor.',
        isEditMode ? 'update' : 'create'
      )
      toast.error(msg, { position: 'top-center' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderMappingFields = (mapping: MappingForm, index: number, useContainer: boolean) => {
    const term = regionSearchTerms[mapping.clientId] || ''
    const results = regionSearchResults[mapping.clientId] || []
    const isLoadingRegions = regionSearchLoading[mapping.clientId]
    const showNoResults = !isLoadingRegions && term.trim().length >= 2 && results.length === 0
    const block = (
      <div className='row g-3'>
        {canManageMultiCompanyMappings && (
          <>
            <div className='col-12'>
              <label className={formLabelClassName}>Company Group</label>
              <SearchableSelect
                aria-label='Company Group'
                className='form-select'
                value={mapping.companyGroupAdminId ?? ''}
                disabled={isLoadingCompanies}
                onChange={(e) =>
                  updateMapping(mapping.clientId, {
                    companyGroupAdminId: e.target.value ? Number(e.target.value) : null,
                  })
                }
              >
                <option value=''>Select company group</option>
                {(companyGroups ?? []).map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </SearchableSelect>
              {errors[`mapping-${index}-scope`] && <div className={errorTextClassName}>{errors[`mapping-${index}-scope`]}</div>}
              {errors[`mapping-${index}-duplicate`] && <div className={errorTextClassName}>{errors[`mapping-${index}-duplicate`]}</div>}
            </div>
          </>
        )}

        <div className='col-md-6'>
          <label className={`${formLabelClassName} required`}>Vendor Code</label>
          <input
            aria-label={canManageMultiCompanyMappings ? 'Mapping Code' : 'Vendor Code'}
            className={`form-control ${errors[`mapping-${index}-code`] || errors.code ? 'is-invalid' : ''}`}
            placeholder='e.g. VEN-001'
            value={mapping.code}
            onChange={(e) => {
              updateMapping(mapping.clientId, { code: e.target.value })
              updateGlobalField('code', e.target.value)
            }}
          />
          {errors[`mapping-${index}-code`] && <div className={errorTextClassName}>{errors[`mapping-${index}-code`]}</div>}
        </div>
        <div className='col-md-6'>
          <label className={formLabelClassName}>Vendor Initials</label>
          <input
            aria-label='Vendor Initials'
            className='form-control'
            placeholder='e.g. OSM'
            value={mapping.vendorInitials || ''}
            onChange={(e) => {
              updateMapping(mapping.clientId, { vendorInitials: e.target.value })
              updateGlobalField('vendorInitials', e.target.value)
            }}
          />
        </div>
        <div className='col-md-6'>
          <label className={`${formLabelClassName} required`}>Vendor Name</label>
          <input
            aria-label={canManageMultiCompanyMappings ? 'Mapping Name' : 'Vendor Name'}
            className={`form-control ${errors[`mapping-${index}-name`] || errors.name ? 'is-invalid' : ''}`}
            value={mapping.name}
            onChange={(e) => {
              updateMapping(mapping.clientId, { name: e.target.value })
              updateGlobalField('name', e.target.value)
            }}
          />
          {errors[`mapping-${index}-name`] && <div className={errorTextClassName}>{errors[`mapping-${index}-name`]}</div>}
        </div>

        <div className='col-md-6'>
          <label className={formLabelClassName}>Address</label>
          <textarea
            className='form-control'
            rows={3}
            value={mapping.address || ''}
            onChange={(e) => {
              updateMapping(mapping.clientId, { address: e.target.value })
              updateGlobalField('address', e.target.value)
            }}
          />
        </div>
        <div className='col-md-6'>
          <label className={formLabelClassName}>Payment Terms</label>
          <textarea
            className='form-control'
            rows={3}
            value={mapping.paymentTerms || ''}
            onChange={(e) => {
              updateMapping(mapping.clientId, { paymentTerms: e.target.value })
              updateGlobalField('paymentTerms', e.target.value)
            }}
          />
        </div>

        <div className='col-12'>
          <h6 className={sectionHeadingClassName}>Categories</h6>
          <div className='border rounded p-3' style={{ ...mutedCardStyle, maxHeight: '180px', overflowY: 'auto' }}>
            {(vendorCategories ?? []).map((category) => (
              <div key={`${mapping.clientId}-${category.id}`} className='form-check mb-2'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id={`${mapping.clientId}-category-${category.id}`}
                  checked={mapping.categoryIds.includes(category.id)}
                  onChange={() => toggleCategory(mapping.clientId, category.id)}
                />
                <label className='form-check-label text-gray-800' htmlFor={`${mapping.clientId}-category-${category.id}`}>
                  {category.name}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className='col-12'>
          <h6 className={sectionHeadingClassName}>Service Regions</h6>
          <label className={formLabelClassName}>Service Regions</label>
          <input
            aria-label='Service Regions'
            className='form-control mb-2'
            placeholder='Search continent, country, or city'
            value={term}
            onChange={(e) => {
              void handleRegionSearch(mapping.clientId, e.target.value)
            }}
          />
          <div className={helperTextClassName}>
            Search canonical continent, country, or city records. Type at least 2 characters.
          </div>
          {isLoadingRegions && <div className='small text-muted mt-2'>Loading regions...</div>}
          {showNoResults && <div className='small text-muted mt-2'>No matching regions found.</div>}
          {!isLoadingRegions && results.length > 0 && (
            <div className='border rounded p-2 mt-2 mb-2' style={{ ...mutedCardStyle, maxHeight: '180px', overflowY: 'auto' }}>
              {results.map((region) => (
                <button
                  key={`${mapping.clientId}-region-${region.id}`}
                  type='button'
                  className='btn btn-sm btn-light w-100 text-start mb-1'
                  onClick={() => addServiceRegion(mapping.clientId, region)}
                >
                  {region.path}
                </button>
              ))}
            </div>
          )}
          <div className='d-flex flex-wrap gap-2 mt-2'>
            {(mapping.serviceRegions || []).map((region) => (
              <span key={`${mapping.clientId}-selected-region-${region.id}`} className='badge bg-light text-dark border'>
                {region.path}
                <button
                  type='button'
                  className='btn btn-sm ms-2 p-0 text-dark'
                  onClick={() => removeServiceRegion(mapping.clientId, region.id)}
                >
                  x
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className='col-12'>
          <div className='d-flex justify-content-between align-items-center mb-2'>
            <h6 className={`${sectionHeadingClassName} mb-0`}>Contacts</h6>
            <button type='button' className='btn btn-sm btn-light-primary' onClick={() => addContact(mapping.clientId)}>
              Add Contact
            </button>
          </div>
          {mapping.contacts.map((contact, contactIndex) => (
            <div key={`${mapping.clientId}-contact-${contactIndex}`} className='border rounded p-3 mb-3' style={mutedCardStyle}>
              <div className='row g-3'>
                <div className='col-md-3'>
                  <label className={formLabelClassName}>Contact Name</label>
                  <input
                    className={`form-control ${errors[`mapping-${index}-contact-${contactIndex}-name`] ? 'is-invalid' : ''}`}
                    placeholder='Contact name'
                    value={contact.name}
                    onChange={(e) => updateContact(mapping.clientId, contactIndex, { name: e.target.value })}
                  />
                  {errors[`mapping-${index}-contact-${contactIndex}-name`] && (
                    <div className={errorTextClassName}>{errors[`mapping-${index}-contact-${contactIndex}-name`]}</div>
                  )}
                </div>
                <div className='col-md-3'>
                  <label className={formLabelClassName}>Contact Email</label>
                  <input
                    className='form-control'
                    placeholder='Email'
                    value={contact.email}
                    onChange={(e) => updateContact(mapping.clientId, contactIndex, { email: e.target.value })}
                  />
                </div>
                <div className='col-md-2'>
                  <label className={formLabelClassName}>Phone</label>
                  <input
                    className='form-control'
                    placeholder='Phone'
                    value={contact.phone}
                    onChange={(e) => updateContact(mapping.clientId, contactIndex, { phone: e.target.value })}
                  />
                </div>
                <div className='col-md-2'>
                  <label className={formLabelClassName}>Designation</label>
                  <input
                    className='form-control'
                    placeholder='Designation'
                    value={contact.designation}
                    onChange={(e) => updateContact(mapping.clientId, contactIndex, { designation: e.target.value })}
                  />
                </div>
                <div className='col-md-2 d-flex align-items-end justify-content-between'>
                  <div className='form-check'>
                    <input
                      className='form-check-input'
                      type='radio'
                      checked={contact.primaryContact}
                      onChange={() => updateContact(mapping.clientId, contactIndex, { primaryContact: true })}
                    />
                    <label className='form-check-label text-gray-800'>Primary</label>
                  </div>
                  <button type='button' className='btn btn-sm btn-light-danger' onClick={() => removeContact(mapping.clientId, contactIndex)}>
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
          {errors[`mapping-${index}-contacts`] && <div className={errorTextClassName}>{errors[`mapping-${index}-contacts`]}</div>}
        </div>
      </div>
    )

    if (!useContainer) {
      return <div key={mapping.clientId} className='mb-4'>{block}</div>
    }

    return (
      <div key={mapping.clientId} className='border rounded p-4 mb-4'>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <h6 className='mb-0 text-gray-900'>Mapping {index + 1}</h6>
          <button type='button' className='btn btn-sm btn-light-danger' onClick={() => removeMapping(mapping.clientId)}>
            Remove Mapping
          </button>
        </div>
        {block}
      </div>
    )
  }

  if (!visible) return null

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1050 }}
    >
      <div className='modal-dialog modal-xl modal-dialog-centered' role='document' style={{ maxHeight: '95vh', margin: '1.75rem auto' }}>
        <div className='modal-content bg-white' style={{ display: 'flex', flexDirection: 'column', maxHeight: '95vh', color: 'var(--bs-gray-900, #181c32)' }}>
          <form autoComplete='off' onSubmit={(e) => e.preventDefault()} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div className='modal-header' style={{ flexShrink: 0 }}>
              <h5 className='modal-title text-gray-900'>{isEditMode ? 'Edit Vendor' : 'Add New Vendor'}</h5>
              <button type='button' className='btn-close' onClick={onClose}></button>
            </div>

            <div className='modal-body' style={{ overflowY: 'auto', flexGrow: 1 }}>
              <div className='row g-3 mb-4'>
                <div className='col-md-6'>
                  <label className={`${formLabelClassName} required`}>Vendor Code</label>
                  <input
                    aria-label='Vendor Code'
                    className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                    value={globalForm.code}
                    onChange={(e) => {
                      updateGlobalField('code', e.target.value)
                      if (!canManageMultiCompanyMappings) updateMapping(scopedMapping.clientId, { code: e.target.value })
                    }}
                  />
                  {errors.code && <div className={errorTextClassName}>{errors.code}</div>}
                </div>
                <div className='col-md-6'>
                  <label className={`${formLabelClassName} required`}>Vendor Name</label>
                  <input
                    aria-label='Vendor Name'
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    value={globalForm.name}
                    onChange={(e) => {
                      updateGlobalField('name', e.target.value)
                      if (!canManageMultiCompanyMappings) updateMapping(scopedMapping.clientId, { name: e.target.value })
                    }}
                  />
                  {errors.name && <div className={errorTextClassName}>{errors.name}</div>}
                </div>
                <div className='col-md-6'>
                  <label className={`${formLabelClassName} required`}>Email</label>
                  <input
                    aria-label='Email'
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    type='email'
                    value={globalForm.email}
                    onChange={(e) => updateGlobalField('email', e.target.value)}
                  />
                  {errors.email && <div className={errorTextClassName}>{errors.email}</div>}
                </div>
                <div className='col-md-6'>
                  <label className={`${formLabelClassName} ${!isEditMode ? 'required' : ''}`}>Password</label>
                  <input
                    aria-label='Password'
                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                    type='password'
                    value={globalForm.password}
                    onChange={(e) => updateGlobalField('password', e.target.value)}
                  />
                  {errors.password && <div className={errorTextClassName}>{errors.password}</div>}
                </div>
                <div className='col-md-6'>
                  <label className={`${formLabelClassName} required`}>Primary Contact Number</label>
                  <input
                    aria-label='Primary Contact Number'
                    className={`form-control ${errors.contactNo ? 'is-invalid' : ''}`}
                    value={globalForm.contactNo}
                    onChange={(e) => updateGlobalField('contactNo', e.target.value)}
                  />
                  {errors.contactNo && <div className={errorTextClassName}>{errors.contactNo}</div>}
                </div>
                <div className='col-md-6'>
                  <label className={formLabelClassName}>Alternate Contact</label>
                  <input aria-label='Alternate Contact' className='form-control' value={globalForm.altContactNo} onChange={(e) => updateGlobalField('altContactNo', e.target.value)} />
                </div>
                <div className='col-md-6'>
                  <label className={formLabelClassName}>Preferred Currency</label>
                  <CurrencySelect
                    ariaLabel='Preferred Currency'
                    className='form-select'
                    currencies={currencies}
                    value={(globalForm as any).preferredCurrencyCode || ''}
                    onChange={(value) => updateGlobalField('preferredCurrencyCode', value)}
                  />
                </div>
              </div>

              <div className='mb-4'>
                <h6 className={sectionHeadingClassName}>Bank Details</h6>
                <div className='row g-3'>
                  <div className='col-md-6'>
                    <label className={formLabelClassName}>Beneficiary / Account Holder Name</label>
                    <input aria-label='Beneficiary / Account Holder Name' className='form-control' value={globalForm.bankBeneficiaryName} onChange={(e) => updateGlobalField('bankBeneficiaryName', e.target.value)} />
                  </div>
                  <div className='col-md-6'>
                    <label className={formLabelClassName}>Bank Name</label>
                    <input aria-label='Bank Name' className='form-control' value={globalForm.bankName} onChange={(e) => updateGlobalField('bankName', e.target.value)} />
                  </div>
                  <div className='col-md-6'>
                    <label className={formLabelClassName}>Account Number</label>
                    <input aria-label='Account Number' className='form-control' value={globalForm.bankAccountNumber} onChange={(e) => updateGlobalField('bankAccountNumber', e.target.value)} />
                  </div>
                  <div className='col-md-6'>
                    <label className={formLabelClassName}>IFSC Code</label>
                    <input aria-label='IFSC Code' className='form-control' value={globalForm.bankIfscCode} onChange={(e) => updateGlobalField('bankIfscCode', e.target.value)} />
                  </div>
                  <div className='col-md-6'>
                    <label className={formLabelClassName}>SWIFT / BIC Code</label>
                    <input aria-label='SWIFT / BIC Code' className='form-control' value={globalForm.bankSwiftCode} onChange={(e) => updateGlobalField('bankSwiftCode', e.target.value)} />
                  </div>
                  <div className='col-md-6'>
                    <label className={formLabelClassName}>IBAN</label>
                    <input aria-label='IBAN' className='form-control' value={globalForm.bankIban} onChange={(e) => updateGlobalField('bankIban', e.target.value)} />
                  </div>
                  <div className='col-md-6'>
                    <label className={formLabelClassName}>Branch Name</label>
                    <input aria-label='Branch Name' className='form-control' value={globalForm.bankBranch} onChange={(e) => updateGlobalField('bankBranch', e.target.value)} />
                  </div>
                  <div className='col-md-6'>
                    <label className={formLabelClassName}>Account Currency</label>
                    <CurrencySelect ariaLabel='Account Currency' className='form-select' currencies={currencies} value={globalForm.bankAccountCurrency} onChange={(value) => updateGlobalField('bankAccountCurrency', value)} />
                  </div>
                  <div className='col-12'>
                    <label className={formLabelClassName}>Bank Address</label>
                    <textarea aria-label='Bank Address' className='form-control' rows={3} value={globalForm.bankAddress} onChange={(e) => updateGlobalField('bankAddress', e.target.value)} />
                  </div>
                </div>
              </div>

              {canManageMultiCompanyMappings ? (
                <>
                  <div className='d-flex justify-content-between align-items-center mb-3'>
                    <h6 className='mb-0 text-gray-900'>Company Mappings</h6>
                    <button type='button' className='btn btn-sm btn-light-primary' onClick={addMapping}>
                      Add Mapping
                    </button>
                  </div>
                  {mappings.map((mapping, index) => renderMappingFields(mapping, index, true))}
                </>
              ) : (
                <>
                  <h6 className='mb-3 text-gray-900'>Vendor Details</h6>
                  {renderMappingFields(scopedMapping, 0, false)}
                </>
              )}

              {errors.companyMappings && <div className={errorTextClassName}>{errors.companyMappings}</div>}
              {isScopedActor && (
                <div className='small text-muted mt-2'>
                  Vendor ownership is derived from your {actorBehavior === 'COMPANY_ADMIN' ? 'company' : 'company group'} scope.
                </div>
              )}
            </div>

            <div className='modal-footer' style={{ flexShrink: 0 }}>
              <button type='button' className='btn btn-light' onClick={onClose} disabled={isSubmitting}>
                Cancel
              </button>
              <button type='button' className='btn btn_primary d-flex align-items-center gap-2' onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting && <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />}
                {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Vendor' : 'Create Vendor')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AddVendorModal
